import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSitemap,
  faPen,
  faPlus,
  faTrash,
  faHouse,
  faChevronRight,
  faArrowUp,
  faArrowDown,
  faChevronDown,
  faChevronRight as faChevronRightSm,
  faXmark,
  faFileExport,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { useToast } from "../../../components/ui";
import {
  AssetTableItem,
  AssetSection,
  AssetDiceTemplate,
  AssetDiceTemplateRequest,
} from "../../../types/types";
import { apiFetch } from "../../../services/apiClient";
import { getTypeConfig } from "./typeConfig";
import { findAncestors } from "./utils";
import AssetMarkdownField from "./AssetMarkdownField";
import { ConfirmationDialog } from "../../../components/ui";
import DiceTemplateManager from "../../../components/business/DiceTemplate/DiceTemplateManager";
import { AssetService } from "../../../services/assetService";
import DmNotesField from "../../../components/business/DmNotesField";
import { Tooltip } from "../../../components/ui";
import AssetEditModal from "./AssetEditModal";

export interface AssetDetailPanelProps {
  /** Currently selected asset (full detail). Null shows empty state. */
  asset: AssetTableItem | null;
  /** Flat list of all assets for autocomplete + chip lookup. */
  allAssets: AssetTableItem[];
  /** Root hierarchy used to compute breadcrumb ancestors. */
  hierarchyRoot: AssetTableItem[];
  /** Called when the user clicks a reference chip or hierarchy navigation button. */
  onNavigate: (_id: number) => void;
  /** Open create-asset modal with a pre-filled parentId. */
  onAddChild: (_parentId: number) => void;
  /** Trigger delete flow for an asset. */
  onDelete: (_id: number) => void;
  /** Re-fetch the selected asset's detail from the server (e.g. after addSection to get server-assigned ID). */
  onRefreshDetail: () => void;
  /** Called after a successful inline edit with the updated asset. */
  onEdit?: (_updated: AssetTableItem) => void;
}

const AssetDetailPanel: React.FC<AssetDetailPanelProps> = ({
  asset,
  allAssets,
  hierarchyRoot,
  onNavigate,
  onAddChild,
  onDelete,
  onRefreshDetail,
  onEdit,
}) => {
  const toast = useToast();

  // ── Tag state ────────────────────────────────────────────────────────────────
  /** Local copy of tags for optimistic updates without waiting for full refresh. */
  const [localTags, setLocalTags] = useState<string[]>([]);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [tagInput, setTagInput] = useState("");
  const [savingTags, setSavingTags] = useState(false);
  const tagInputRef = useRef<HTMLInputElement>(null);

  // Sections sorted by sectionOrder (local mutable copy so reorder is instant)
  const [sortedSections, setSortedSections] = useState<AssetSection[]>([]);
  // Which sections are expanded in the accordion
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());
  // Add-section form visibility
  const [showAddSection, setShowAddSection] = useState(false);
  const [newSectionName, setNewSectionName] = useState("");
  const [newSectionContent, setNewSectionContent] = useState("");
  const [addingSect, setAddingSect] = useState(false);
  // Inline delete confirmation for sections
  const [deleteSectionConfirm, setDeleteSectionConfirm] = useState<number | null>(null);
  // Asset delete modal
  const [deleteAssetModalOpen, setDeleteAssetModalOpen] = useState(false);
  // Asset edit modal
  const [editAssetModalOpen, setEditAssetModalOpen] = useState(false);
  // Track which section is currently in content-edit mode (so we can show name edit)
  const [editingContentSectionId, setEditingContentSectionId] = useState<number | null>(null);
  // Draft name for the section being edited
  const [sectionNameDraft, setSectionNameDraft] = useState("");
  // Export state
  const [exportMenuOpen, setExportMenuOpen] = useState(false);

  // ── Local mutable copy of the asset's scalar fields ─────────────────────────
  // Allows optimistic updates without waiting for a full detail re-fetch.
  const [localDescription, setLocalDescription] = useState<string>("");
  const [localDmNotes, setLocalDmNotes] = useState<string>("");

  // Fate Formulas
  const supportsFateFormulas = (type: string) =>
    type === "Character" || type === "Beast" || type === "NPC";
  const [diceTemplates, setDiceTemplates] = useState<AssetDiceTemplate[]>([]);
  const [diceTemplatesLoading, setDiceTemplatesLoading] = useState(false);

  useEffect(() => {
    if (asset) {
      const sorted = [...(asset.sections ?? [])].sort(
        (a, b) => (a.sectionOrder ?? 0) - (b.sectionOrder ?? 0)
      );
      setSortedSections(sorted);
      setExpandedSections(new Set()); // collapsed by default
      setLocalTags(asset.tags ?? []);
      setLocalDescription(asset.description ?? "");
      setLocalDmNotes(asset.dmNotes ?? "");
    } else {
      setSortedSections([]);
      setExpandedSections(new Set());
      setLocalTags([]);
      setLocalDescription("");
      setLocalDmNotes("");
    }
    setShowAddSection(false);
    setNewSectionName("");
    setNewSectionContent("");
    setEditingContentSectionId(null);
    setSectionNameDraft("");
    setIsAddingTag(false);
    setTagInput("");
  }, [asset]);

  // Load dice templates when asset changes
  useEffect(() => {
    if (!asset || !supportsFateFormulas(asset.type)) {
      setDiceTemplates([]);
      return;
    }
    setDiceTemplatesLoading(true);
    AssetService.getAssetDiceTemplates(asset.id)
      .then((data) => setDiceTemplates(data ?? []))
      .catch(() => setDiceTemplates([]))
      .finally(() => setDiceTemplatesLoading(false));
  }, [asset]);

  // ── Breadcrumb ──────────────────────────────────────────────────────────────
  const breadcrumb = asset ? findAncestors(hierarchyRoot, asset.id) : [];

  // ── Description save ───────────────────────────────────────────────────────
  const saveDescription = async (newValue: string) => {
    if (!asset) return;
    const res = await apiFetch("/api/protected/assets/asset", {
      method: "PUT",
      body: JSON.stringify({
        id: asset.id,
        name: asset.name,
        type: asset.type,
        category: asset.category,
        description: newValue,
        tags: asset.tags ?? [],
        parentId: asset.parentId ?? null,
      }),
    });
    if (!res.ok) {
      toast.showToast({ type: "error", message: "Failed to save description." });
      throw new Error("save failed");
    }
    // Update local state immediately — no re-fetch needed
    setLocalDescription(newValue);
  };

  // ── DM Notes save ──────────────────────────────────────────────────────────
  const saveDmNotes = async (dmNotes: string) => {
    if (!asset) return;
    const res = await apiFetch("/api/protected/assets/asset", {
      method: "PUT",
      body: JSON.stringify({
        id: asset.id,
        name: asset.name,
        type: asset.type,
        category: asset.category,
        description: asset.description,
        dmNotes,
        tags: asset.tags ?? [],
        parentId: asset.parentId ?? null,
      }),
    });
    if (!res.ok) {
      toast.showToast({ type: "error", message: "Failed to save DM notes." });
      throw new Error("save failed");
    }
    // Update local state immediately — no re-fetch needed
    setLocalDmNotes(dmNotes);
  };

  // ── Tag helpers ─────────────────────────────────────────────────────────────
  const persistTags = async (newTags: string[]) => {
    if (!asset) return;
    setSavingTags(true);
    const previous = localTags;
    setLocalTags(newTags); // optimistic
    try {
      const res = await apiFetch("/api/protected/assets/asset", {
        method: "PUT",
        body: JSON.stringify({
          id: asset.id,
          name: asset.name,
          type: asset.type,
          category: asset.category,
          description: asset.description,
          tags: newTags,
          parentId: asset.parentId ?? null,
        }),
      });
      if (!res.ok) {
        setLocalTags(previous); // revert
        toast.showToast({ type: "error", message: "Failed to save tags." });
        return;
      }
      // Tags are already updated optimistically — no re-fetch needed
    } finally {
      setSavingTags(false);
    }
  };

  const commitTag = (raw: string) => {
    const tag = raw.trim().toLowerCase();
    if (!tag || localTags.includes(tag)) {
      setTagInput("");
      setIsAddingTag(false);
      return;
    }
    const newTags = [...localTags, tag];
    setTagInput("");
    setIsAddingTag(false);
    persistTags(newTags);
  };

  const removeTag = (tag: string) => {
    persistTags(localTags.filter((t) => t !== tag));
  };

  // ── Section save ───────────────────────────────────────────────────────────
  const saveSection = async (section: AssetSection, newContent: string, newName?: string) => {
    if (!asset) return;
    const res = await apiFetch("/api/protected/assets/asset/section", {
      method: "PUT",
      body: JSON.stringify({
        id: section.id,
        name: newName ?? section.name,
        description: newContent,
        assetId: asset.id,
        sectionOrder: section.sectionOrder ?? 0,
      }),
    });
    if (!res.ok) {
      toast.showToast({ type: "error", message: "Failed to save folio." });
      throw new Error("save failed");
    }
    // Update local sections state immediately — no re-fetch needed
    setSortedSections((prev) =>
      prev.map((s) =>
        s.id === section.id ? { ...s, description: newContent, name: newName ?? s.name } : s
      )
    );
  };

  // ── Section DM Notes save ──────────────────────────────────────────────────
  const saveSectionDmNotes = async (section: AssetSection, dmNotes: string) => {
    if (!asset) return;
    const res = await apiFetch("/api/protected/assets/asset/section", {
      method: "PUT",
      body: JSON.stringify({
        id: section.id,
        name: section.name,
        description: section.description,
        dmNotes,
        assetId: asset.id,
        sectionOrder: section.sectionOrder ?? 0,
      }),
    });
    if (!res.ok) {
      toast.showToast({ type: "error", message: "Failed to save section DM notes." });
      throw new Error("save failed");
    }
    // Update local sections state immediately — no re-fetch needed
    setSortedSections((prev) => prev.map((s) => (s.id === section.id ? { ...s, dmNotes } : s)));
  };

  // ── Section delete ─────────────────────────────────────────────────────────
  const deleteSection = async (sectionId: number) => {
    const res = await apiFetch(`/api/protected/assets/asset/section/${sectionId}`, {
      method: "DELETE",
    });
    if (!res.ok) {
      toast.showToast({ type: "error", message: "Failed to delete folio." });
      return;
    }
    setDeleteSectionConfirm(null);
    // Remove from local state immediately — no re-fetch needed
    setSortedSections((prev) => prev.filter((s) => s.id !== sectionId));
  };

  // ── Section reorder ────────────────────────────────────────────────────────
  const reorderSection = async (sectionId: number, direction: "up" | "down") => {
    if (!asset) return;
    const idx = sortedSections.findIndex((s) => s.id === sectionId);
    if (idx === -1) return;
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sortedSections.length) return;

    // Swap in local state immediately
    const newSections = [...sortedSections];
    const tempOrder = newSections[idx].sectionOrder;
    newSections[idx] = { ...newSections[idx], sectionOrder: newSections[swapIdx].sectionOrder };
    newSections[swapIdx] = { ...newSections[swapIdx], sectionOrder: tempOrder };
    newSections.sort((a, b) => (a.sectionOrder ?? 0) - (b.sectionOrder ?? 0));
    setSortedSections(newSections);

    // Persist new ordering
    const payload = newSections.map((s, i) => ({ sectionId: s.id, order: i }));
    const res = await apiFetch(`/api/protected/assets/asset/${asset.id}/sections/reorder`, {
      method: "PUT",
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      toast.showToast({ type: "error", message: "Failed to reorder sections." });
      onRefreshDetail(); // revert to server state
    }
  };

  // ── Add section ────────────────────────────────────────────────────────────
  const addSection = async () => {
    if (!asset || !newSectionName.trim()) return;
    setAddingSect(true);
    try {
      const res = await apiFetch("/api/protected/assets/asset/section", {
        method: "POST",
        body: JSON.stringify({
          name: newSectionName.trim(),
          description: newSectionContent,
          assetId: asset.id,
          sectionOrder: sortedSections.length,
        }),
      });
      if (!res.ok) {
        toast.showToast({ type: "error", message: "Failed to add section." });
        return;
      }
      setShowAddSection(false);
      setNewSectionName("");
      setNewSectionContent("");
      // Re-fetch detail to capture the server-assigned section ID
      onRefreshDetail();
    } finally {
      setAddingSect(false);
    }
  };

  // ── Export ─────────────────────────────────────────────────────────────────
  const handleExport = async (includeChildren: boolean) => {
    if (!asset) return;
    setExportMenuOpen(false);
    try {
      const url = `/api/protected/assets/asset/${asset.id}/export${includeChildren ? "?includeChildren=true" : ""}`;
      const res = await apiFetch(url);
      if (!res.ok) {
        toast.showToast({ type: "error", message: "Failed to export asset." });
        return;
      }
      const blob = await res.blob();
      const disposition = res.headers.get("Content-Disposition") ?? "";
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `asset-${asset.id}.json`;
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => {
        try {
          document.body.removeChild(link);
        } catch {
          // ignore if already removed
        }
        URL.revokeObjectURL(objectUrl);
      }, 0);
    } catch {
      toast.showToast({ type: "error", message: "Failed to export asset." });
    }
  };

  // ── Empty state ────────────────────────────────────────────────────────────
  if (!asset) {
    return (
      <div className="flex flex-col h-full">
        {/* Empty breadcrumb bar */}
        <div className="px-6 py-2.5 bg-zinc-900/50 border-b border-zinc-800 flex items-center gap-1 text-xs text-zinc-500 flex-shrink-0 min-h-10">
          <FontAwesomeIcon icon={faHouse} className="text-zinc-600 text-xs mr-1" />
          <span className="text-zinc-600 flex-shrink-0">Compendium</span>
          <FontAwesomeIcon icon={faChevronRight} className="text-zinc-700 text-[10px] mx-1.5" />
          <span className="text-zinc-500 italic">Nothing selected</span>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
          <div className="w-20 h-20 bg-zinc-900 rounded-2xl flex items-center justify-center mb-5 border border-zinc-800">
            <FontAwesomeIcon icon={faSitemap} className="text-zinc-700 text-3xl" />
          </div>
          <p className="text-zinc-400 text-sm font-medium mb-1">No entry selected</p>
          <p className="text-zinc-600 text-xs">Click any node in the tree to view its details</p>
        </div>
      </div>
    );
  }

  const cfg = getTypeConfig(asset.type);

  return (
    <div className="flex flex-col h-full">
      {/* ── Breadcrumb ── */}
      <div className="px-6 py-2.5 bg-zinc-900/50 border-b border-zinc-800 flex items-center gap-1 text-xs text-zinc-500 flex-shrink-0 min-h-10 overflow-hidden">
        <FontAwesomeIcon icon={faHouse} className="text-zinc-600 text-xs mr-1 flex-shrink-0" />
        <span className="text-zinc-600 flex-shrink-0">Compendium</span>
        {breadcrumb.map((ancestor, i) => {
          const aCfg = getTypeConfig(ancestor.type);
          const isLast = i === breadcrumb.length - 1;
          return (
            <React.Fragment key={ancestor.id}>
              <FontAwesomeIcon
                icon={faChevronRight}
                className="text-zinc-700 text-[10px] mx-1.5 flex-shrink-0"
              />
              {isLast ? (
                <span className="text-zinc-200 font-medium truncate max-w-48">{ancestor.name}</span>
              ) : (
                <button
                  onClick={() => onNavigate(ancestor.id)}
                  className={`hover:text-white transition-colors truncate max-w-32 ${aCfg.color}`}
                >
                  {ancestor.name}
                </button>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* ── Detail body ── */}
      <div className="flex-1 overflow-y-auto p-6 scrollbar-modern">
        <div className="max-w-2xl space-y-8">
          {/* ── Asset header ── */}
          <div className="flex items-start gap-5">
            <div
              className={`w-16 h-16 ${cfg.bg} border ${cfg.border} rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg`}
            >
              <FontAwesomeIcon icon={cfg.icon} className={`${cfg.color} text-2xl`} />
            </div>
            <div className="flex-1 min-w-0 pt-1">
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-2xl font-semibold leading-tight truncate">{asset.name}</h1>
                {asset.isFromMarketplace && (
                  <Tooltip content="Acquired from Marketplace">
                    <span className="text-amber-500 text-lg flex-shrink-0">
                      <FontAwesomeIcon icon={faStore} />
                    </span>
                  </Tooltip>
                )}
              </div>
              <div className="flex items-center gap-2 text-xs text-zinc-400">
                <span className={`${cfg.color} font-medium`}>{cfg.label}</span>
                {asset.category && (
                  <>
                    <span className="text-zinc-700">·</span>
                    <span>{asset.category}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={() => setEditAssetModalOpen(true)}
                className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded text-zinc-300 transition-colors flex items-center gap-1.5"
                data-testid="edit-asset-btn"
              >
                <FontAwesomeIcon icon={faPen} className="text-xs" />
                Edit
              </button>
              <button
                onClick={() => onAddChild(asset.id)}
                className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded text-zinc-300 transition-colors flex items-center gap-1.5"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                Child
              </button>
              {/* Export dropdown */}
              <div className="relative">
                <button
                  onClick={() => setExportMenuOpen((v) => !v)}
                  disabled={asset.isFromMarketplace}
                  className="px-3 py-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 rounded text-zinc-300 transition-colors flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-zinc-800"
                  title={
                    asset.isFromMarketplace
                      ? "Marketplace assets can't be exported."
                      : "Export this asset"
                  }
                >
                  <FontAwesomeIcon icon={faFileExport} className="text-xs" />
                  Export
                </button>
                {exportMenuOpen && !asset.isFromMarketplace && (
                  <div className="absolute right-0 mt-1 w-52 bg-zinc-800 border border-zinc-700 rounded shadow-xl z-50 py-1 text-sm">
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-zinc-700 text-zinc-300 transition-colors"
                      onClick={() => handleExport(false)}
                    >
                      Export this asset only
                    </button>
                    <button
                      className="w-full text-left px-4 py-2 hover:bg-zinc-700 text-zinc-300 transition-colors"
                      onClick={() => handleExport(true)}
                    >
                      Export with children
                    </button>
                  </div>
                )}
              </div>
              <button
                onClick={() => setDeleteAssetModalOpen(true)}
                className="px-3 py-1.5 text-xs bg-red-600/20 hover:bg-red-600/30 border border-red-600/40 rounded text-red-400 transition-colors flex items-center gap-1.5"
              >
                <FontAwesomeIcon icon={faTrash} className="text-xs" />
                Delete
              </button>
            </div>
          </div>

          {/* ── Tags ── */}
          <div>
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3">
              Tags
            </h3>
            <div className="flex flex-wrap items-center gap-2">
              {localTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 text-xs bg-zinc-800 text-zinc-300 px-2.5 py-1 rounded-full border border-zinc-700 group"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    disabled={savingTags}
                    className="text-zinc-600 hover:text-red-400 transition-colors disabled:opacity-40"
                    aria-label={`Remove tag ${tag}`}
                  >
                    <FontAwesomeIcon icon={faXmark} className="text-[9px]" />
                  </button>
                </span>
              ))}

              {/* Inline tag input */}
              {isAddingTag ? (
                <input
                  ref={tagInputRef}
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      commitTag(tagInput);
                    } else if (e.key === "Escape") {
                      setTagInput("");
                      setIsAddingTag(false);
                    } else if (e.key === "Backspace" && tagInput === "" && localTags.length > 0) {
                      removeTag(localTags[localTags.length - 1]);
                    }
                  }}
                  onBlur={() => {
                    if (tagInput.trim()) {
                      commitTag(tagInput);
                    } else {
                      setIsAddingTag(false);
                    }
                  }}
                  placeholder="tag name…"
                  autoFocus
                  className="text-xs bg-zinc-800 text-zinc-200 placeholder-zinc-600 border border-zinc-600 focus:border-amber-400/50 rounded-full px-2.5 py-1 outline-none w-28"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setIsAddingTag(true);
                    setTimeout(() => tagInputRef.current?.focus(), 0);
                  }}
                  disabled={savingTags}
                  className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 px-2.5 py-1 rounded-full border border-dashed border-zinc-700 hover:border-zinc-500 transition-colors disabled:opacity-40"
                >
                  <FontAwesomeIcon icon={faPlus} className="text-[9px]" />
                  Add tag
                </button>
              )}
            </div>
          </div>

          {/* ── Description ── */}
          <div>
            <AssetMarkdownField
              label="DESCRIPTION"
              value={localDescription}
              onSave={saveDescription}
              allAssets={allAssets}
              onReferenceNavigate={onNavigate}
            />
          </div>

          {/* ── DM Notes ── */}
          <div>
            <DmNotesField value={localDmNotes} onSave={saveDmNotes} />
          </div>

          {/* ── Sections ── */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest">
                Folios
              </h3>
              {sortedSections.length > 0 && (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setExpandedSections(new Set(sortedSections.map((s) => s.id)))}
                    className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Expand All
                  </button>
                  <span className="text-zinc-700">|</span>
                  <button
                    type="button"
                    onClick={() => setExpandedSections(new Set())}
                    className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    Collapse All
                  </button>
                </div>
              )}
            </div>

            {sortedSections.length === 0 && !showAddSection && (
              <p className="text-zinc-600 text-xs mb-3">No folios yet.</p>
            )}

            <div className="space-y-1">
              {sortedSections.map((section, idx) => {
                const isOpen = expandedSections.has(section.id);
                const showDelete = deleteSectionConfirm === section.id;

                return (
                  <div
                    key={section.id}
                    className="border border-zinc-800 rounded-md overflow-hidden"
                  >
                    {/* Section header */}
                    <div className="flex items-center px-3 py-2 bg-zinc-900/70 gap-2">
                      <button
                        type="button"
                        onClick={() =>
                          setExpandedSections((prev) => {
                            const next = new Set(prev);
                            next.has(section.id) ? next.delete(section.id) : next.add(section.id);
                            return next;
                          })
                        }
                        className="flex items-center gap-2 flex-1 text-left min-w-0"
                      >
                        <FontAwesomeIcon
                          icon={isOpen ? faChevronDown : faChevronRightSm}
                          className="text-zinc-500 text-[10px] flex-shrink-0"
                        />
                        <span className="text-sm font-medium text-zinc-200 truncate">
                          {section.name}
                        </span>
                      </button>

                      {/* Reorder + delete controls */}
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => reorderSection(section.id, "up")}
                          disabled={idx === 0}
                          title="Move up"
                          className="w-6 h-6 flex items-center justify-center text-zinc-600 hover:text-zinc-300 disabled:opacity-30 transition-colors"
                        >
                          <FontAwesomeIcon icon={faArrowUp} className="text-[10px]" />
                        </button>
                        <button
                          type="button"
                          onClick={() => reorderSection(section.id, "down")}
                          disabled={idx === sortedSections.length - 1}
                          title="Move down"
                          className="w-6 h-6 flex items-center justify-center text-zinc-600 hover:text-zinc-300 disabled:opacity-30 transition-colors"
                        >
                          <FontAwesomeIcon icon={faArrowDown} className="text-[10px]" />
                        </button>

                        {showDelete ? (
                          <div className="flex items-center gap-1 ml-1">
                            <button
                              type="button"
                              onClick={() => deleteSection(section.id)}
                              className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-500 transition-colors"
                            >
                              Delete
                            </button>
                            <button
                              type="button"
                              onClick={() => setDeleteSectionConfirm(null)}
                              className="px-2 py-0.5 text-xs border border-zinc-700 text-zinc-400 rounded hover:text-zinc-200 transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setDeleteSectionConfirm(section.id)}
                            title="Delete Folio"
                            className="w-6 h-6 flex items-center justify-center text-zinc-600 hover:text-red-400 transition-colors ml-1"
                          >
                            <FontAwesomeIcon icon={faXmark} className="text-xs" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Section body */}
                    {isOpen && (
                      <div className="px-3 py-3 bg-zinc-950/40">
                        {/* Section name field (shown only while content is in edit mode) */}
                        {editingContentSectionId === section.id && (
                          <div className="mb-2">
                            <label className="block text-[10px] text-zinc-500 uppercase tracking-widest mb-1">
                              Folio Name
                            </label>
                            <input
                              type="text"
                              value={sectionNameDraft}
                              onChange={(e) => setSectionNameDraft(e.target.value)}
                              placeholder="Folio name…"
                              className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
                            />
                          </div>
                        )}
                        <AssetMarkdownField
                          value={section.description ?? ""}
                          onSave={(newContent) =>
                            saveSection(
                              section,
                              newContent,
                              editingContentSectionId === section.id && sectionNameDraft.trim()
                                ? sectionNameDraft.trim()
                                : undefined
                            )
                          }
                          onModeChange={(mode) => {
                            if (mode === "edit") {
                              setEditingContentSectionId(section.id);
                              setSectionNameDraft(section.name);
                            } else {
                              setEditingContentSectionId((prev) =>
                                prev === section.id ? null : prev
                              );
                            }
                          }}
                          allAssets={allAssets}
                          onReferenceNavigate={onNavigate}
                        />
                        <div className="mt-2">
                          <DmNotesField
                            value={section.dmNotes ?? ""}
                            onSave={(dmNotes) => saveSectionDmNotes(section, dmNotes)}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Add section form */}
            {showAddSection ? (
              <div className="mt-3 border border-zinc-700 rounded-md p-3 space-y-2">
                <input
                  type="text"
                  value={newSectionName}
                  onChange={(e) => setNewSectionName(e.target.value)}
                  placeholder="Folio name…"
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:border-amber-400/50"
                />
                <textarea
                  value={newSectionContent}
                  onChange={(e) => setNewSectionContent(e.target.value)}
                  placeholder="Folio content (Markdown)…"
                  rows={4}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded px-3 py-1.5 text-sm text-zinc-200 placeholder-zinc-500 focus:outline-none focus:border-amber-400/50 resize-y font-mono"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={addSection}
                    disabled={addingSect || !newSectionName.trim()}
                    className="px-3 py-1.5 text-xs bg-amber-400 text-zinc-950 rounded font-semibold hover:bg-amber-300 transition-colors disabled:opacity-50"
                  >
                    {addingSect ? "Saving…" : "Save Folio"}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddSection(false);
                      setNewSectionName("");
                      setNewSectionContent("");
                    }}
                    className="px-3 py-1.5 text-xs border border-zinc-700 text-zinc-400 rounded hover:text-zinc-200 transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setShowAddSection(true)}
                className="mt-3 flex items-center gap-2 px-3 py-2 text-xs text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="text-xs" />
                Add Folio
              </button>
            )}
          </div>

          {/* ── Fate Formulas — Beast & Character entries only ── */}
          {asset && supportsFateFormulas(asset.type) && (
            <div>
              <DiceTemplateManager
                templates={diceTemplates.map((t) => ({
                  id: t.id,
                  characterId: t.assetId,
                  name: t.name,
                  diceNotation: t.diceNotation,
                  modifier: t.modifier,
                  description: t.description,
                  skillType: t.skillType,
                  createdAt: t.createdAt,
                  updatedAt: t.updatedAt,
                }))}
                loading={diceTemplatesLoading}
                onCreateTemplate={async (req: AssetDiceTemplateRequest) => {
                  const created = await AssetService.createAssetDiceTemplate(asset.id, req);
                  setDiceTemplates((prev) => [...prev, created]);
                }}
                onUpdateTemplate={async (templateId: number, req: AssetDiceTemplateRequest) => {
                  const updated = await AssetService.updateAssetDiceTemplate(
                    asset.id,
                    templateId,
                    req
                  );
                  setDiceTemplates((prev) => prev.map((t) => (t.id === templateId ? updated : t)));
                }}
                onDeleteTemplate={async (templateId: number) => {
                  await AssetService.deleteAssetDiceTemplate(asset.id, templateId);
                  setDiceTemplates((prev) => prev.filter((t) => t.id !== templateId));
                }}
              />
            </div>
          )}

          {/* ── Hierarchy navigation ── */}
          <div>
            <h3 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest mb-3">
              Hierarchy
            </h3>

            {!asset.parentId && (!asset.children || asset.children.length === 0) ? (
              <p className="text-zinc-600 text-xs">This is a root entry with no children.</p>
            ) : (
              <div className="space-y-3">
                {asset.parentId && (
                  <div>
                    <p className="text-xs text-zinc-600 uppercase tracking-wide mb-1.5">Parent</p>
                    <button
                      onClick={() => onNavigate(asset.parentId!)}
                      className="flex items-center gap-2 px-3 py-2 text-xs bg-zinc-800/70 hover:bg-zinc-800 border border-zinc-700/50 hover:border-zinc-600 rounded-lg transition-all text-zinc-300"
                    >
                      <FontAwesomeIcon icon={faArrowUp} className="text-zinc-500 text-xs" />
                      Go to parent
                    </button>
                  </div>
                )}

                {asset.children && asset.children.length > 0 && (
                  <div>
                    <p className="text-xs text-zinc-600 uppercase tracking-wide mb-1.5">
                      Children ({asset.children.length})
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {asset.children.map((child) => {
                        const cCfg = getTypeConfig(child.type);
                        return (
                          <button
                            key={child.id}
                            onClick={() => onNavigate(child.id)}
                            className="flex items-center gap-2 px-3 py-2 text-xs bg-zinc-800/70 hover:bg-zinc-800 border border-zinc-700/50 hover:border-zinc-600 rounded-lg transition-all group"
                          >
                            <FontAwesomeIcon
                              icon={cCfg.icon}
                              className={`${cCfg.color} text-[10px]`}
                            />
                            <span className="text-zinc-300 group-hover:text-white transition-colors">
                              {child.name}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Asset edit modal ── */}
      <AssetEditModal
        isOpen={editAssetModalOpen}
        asset={asset}
        allAssets={allAssets}
        currentDescription={localDescription}
        currentDmNotes={localDmNotes}
        currentTags={localTags}
        onClose={() => setEditAssetModalOpen(false)}
        onSaved={(updated) => {
          setEditAssetModalOpen(false);
          onEdit?.(updated);
        }}
      />

      {/* ── Asset delete confirmation modal ── */}
      <ConfirmationDialog
        isOpen={deleteAssetModalOpen}
        onCancel={() => setDeleteAssetModalOpen(false)}
        onConfirm={() => {
          setDeleteAssetModalOpen(false);
          onDelete(asset.id);
        }}
        title="Delete Asset"
        message={`Delete "${asset.name}"? This cannot be undone. Child assets will also be deleted.`}
        confirmText="Delete"
        variant="danger"
      />
    </div>
  );
};

export default AssetDetailPanel;
